import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { productQueryOptions } from '@/features/products';
import { ProductPicker } from '@/features/resource-pickers';

import { MAX_DISCOUNT_PRODUCTS } from '../schemas/discount-rules';

import { PickerField } from './picker-field';

/** The title the picker handed back, or a read of the product for ids saved before. */
function ProductChip({ id, title }: { id: string; title: string | undefined }) {
  const { data } = useQuery({ ...productQueryOptions(id), enabled: title === undefined });
  return <span className="max-w-60 truncate">{title ?? data?.title ?? id}</span>;
}

export interface AppliesToProductsFieldProps {
  /** The discount's store: "From collection" in the picker lists its collections. */
  storeId: string;
  value: string[];
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  id?: string;
  'aria-invalid'?: boolean;
}

/**
 * Master products the discount applies to: chips of the chosen ones, then search or Browse to
 * open the shared product picker (up to 500). The picker's result replaces the list.
 */
export function AppliesToProductsField({
  storeId,
  value,
  onChange,
  ...props
}: AppliesToProductsFieldProps) {
  const { t } = useTranslation('discounts');
  const [titles, setTitles] = useState<ReadonlyMap<string, string>>(new Map());

  return (
    <PickerField
      {...props}
      value={value}
      onChange={onChange}
      renderChip={(id) => <ProductChip id={id} title={titles.get(id)} />}
      renderPicker={(picker) => (
        <ProductPicker
          {...picker}
          scope={{ kind: 'org' }}
          storeId={storeId}
          multiple={MAX_DISCOUNT_PRODUCTS}
          initialSelectedIds={value}
          onConfirm={(selection) => {
            setTitles((previous) => {
              const next = new Map(previous);
              for (const item of selection.items) next.set(item.id, item.title);
              return next;
            });
            onChange(selection.ids);
          }}
        />
      )}
      searchPlaceholder={t('appliesTo.searchProducts')}
      browseLabel={t('appliesTo.browse')}
      listLabel={t('appliesTo.selectedProducts')}
      removeLabel={() => t('appliesTo.remove')}
    />
  );
}
